import { AfterViewInit, Directive, ElementRef, HostListener, NgZone, OnDestroy } from '@angular/core';

/**
 * 改行のある入力欄（textarea.textarea-input）を、iPhone・iPad でも手で高さを変えられるようにする。
 * iPhone・iPad の Safari は CSS の resize（右下のつまみ）を表示しないため、右下に自前のつまみを重ねて置き、
 * 指で上下にドラッグして高さを変える。それ以外の端末はブラウザ標準のつまみをそのまま使う。
 * 入力欄を別の要素で囲むと、外部業者の欄（グリッドで並べている）などのレイアウトが崩れるため、
 * つまみは入力欄のすぐ後ろに置き、入力欄の位置と大きさに合わせて右下へ重ねる。
 * また、SP・タブレットでは、入力中に最後の行が見切れないようにする（onInput）。
 */
@Directive({
  selector: 'textarea.textarea-input',
  standalone: true,
})
export class TextareaResizeDirective implements AfterViewInit, OnDestroy {
  // つまみ（指で触れる範囲）の大きさ
  private static readonly handleSize = 24;

  private handle: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  // 位置合わせの基準にしている要素（入力欄が表示されていない間は無いため、表示されたときに見張り始める）
  private observedParent: Element | null = null;
  private startY = 0;
  private startHeight = 0;

  constructor(private el: ElementRef<HTMLTextAreaElement>, private zone: NgZone) {}

  // SP・タブレット：改行のある入力欄は、高さは変えず欄の中でスクロールする。
  // iPhone では欄の中のスクロールで下の余白が考慮されず、最後の行（改行だけ入れた行など）が見切れるため、
  // カーソルが最後にあるときは、入力のたびに欄の一番下（下の余白まで）を表示する
  @HostListener('input')
  onInput(): void {
    const textarea = this.el.nativeElement;
    if (!window.matchMedia('(max-width: 767px), (min-width: 768px) and (max-width: 1023px)').matches) {
      return;
    }
    if (textarea.selectionEnd < textarea.value.length) {
      return;
    }
    // ブラウザがカーソル位置までスクロールした後に、一番下まで表示し直す
    requestAnimationFrame(() => {
      textarea.scrollTop = textarea.scrollHeight;
    });
  }

  ngAfterViewInit(): void {
    if (!TextareaResizeDirective.isIOS()) {
      return;
    }
    // 位置合わせ・ドラッグは画面の更新（変更検知）を必要としないため、Angular の外で動かす
    this.zone.runOutsideAngular(() => this.createHandle());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.place);
    this.handle?.remove();
  }

  private static isIOS(): boolean {
    return /iPhone|iPad|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  private createHandle(): void {
    const textarea = this.el.nativeElement;
    const size = TextareaResizeDirective.handleSize;
    const handle = document.createElement('div');
    handle.className = 'textarea-resize-handle';
    handle.setAttribute('aria-hidden', 'true');
    // ブラウザ標準のつまみと同じく、右下に斜めの線を2本引く
    handle.innerHTML =
      '<svg width="12" height="12" viewBox="0 0 12 12" style="position:absolute;right:4px;bottom:4px">'
      + '<path d="M11 4L4 11M11 8L8 11" stroke="#999" stroke-width="1.2" stroke-linecap="round" fill="none"/></svg>';
    Object.assign(handle.style, {
      position: 'absolute',
      width: `${size}px`,
      height: `${size}px`,
      touchAction: 'none',
      cursor: 'ns-resize',
      zIndex: '1',
    });
    textarea.parentNode?.insertBefore(handle, textarea.nextSibling);
    this.handle = handle;

    handle.addEventListener('pointerdown', this.onPointerDown);
    handle.addEventListener('pointermove', this.onPointerMove);
    handle.addEventListener('pointerup', this.onPointerUp);
    handle.addEventListener('pointercancel', this.onPointerUp);

    // 入力欄の大きさや、上にある項目の高さが変わったら、つまみを入力欄の右下に置き直す。
    // つまみが元の位置に残ると、ページの一番下より下にはみ出して、下の余白が広がるため、
    // ページ全体の高さが変わったときも置き直す
    this.resizeObserver = new ResizeObserver(this.place);
    this.resizeObserver.observe(textarea);
    this.resizeObserver.observe(document.body);
    window.addEventListener('resize', this.place);
    this.place();
  }

  // つまみを入力欄の右下に置く（入力欄と同じ基準の要素からの位置で合わせる）
  private place = (): void => {
    const textarea = this.el.nativeElement;
    const handle = this.handle;
    if (!handle) {
      return;
    }
    if (!textarea.offsetParent) {
      handle.style.display = 'none';
      return;
    }
    if (this.observedParent !== textarea.offsetParent) {
      if (this.observedParent) {
        this.resizeObserver?.unobserve(this.observedParent);
      }
      this.observedParent = textarea.offsetParent;
      this.resizeObserver?.observe(this.observedParent);
    }
    const size = TextareaResizeDirective.handleSize;
    handle.style.display = '';
    handle.style.top = `${textarea.offsetTop + textarea.offsetHeight - size}px`;
    handle.style.left = `${textarea.offsetLeft + textarea.offsetWidth - size}px`;
  };

  private onPointerDown = (event: PointerEvent): void => {
    event.preventDefault();
    this.startY = event.clientY;
    this.startHeight = this.el.nativeElement.offsetHeight;
    this.handle?.setPointerCapture(event.pointerId);
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.handle?.hasPointerCapture(event.pointerId)) {
      return;
    }
    const textarea = this.el.nativeElement;
    // 最小の高さ（CSS の min-height）より小さくはしない
    const minHeight = parseFloat(getComputedStyle(textarea).minHeight) || 0;
    textarea.style.height = `${Math.max(minHeight, this.startHeight + event.clientY - this.startY)}px`;
  };

  private onPointerUp = (event: PointerEvent): void => {
    if (this.handle?.hasPointerCapture(event.pointerId)) {
      this.handle.releasePointerCapture(event.pointerId);
    }
  };
}
